import Vue from 'vue';
import axios from 'axios';
import { ActionTree, GetterTree, Module, MutationTree } from 'vuex';
import { RootState } from '../RootState';
import { IErrors } from '@/model/IErrors';
import { settings } from '@/plugins/settings';
import responseHandler from '@/plugins/errorHandling';
import { IBusinessClient, IShortBusinessClient } from '@/model/BusinessClient/businessClientInterfaces';
import {
  PAGINATION_FIRST_PAGE,
  PAGINATION_DEFAULT_LAST_PAGE,
  PAGINATION_DEFAULT_TOTAL,
  PAGINATION_DEFAULT_PER_PAGE,
} from '@/constants/pagination';

interface BusinessClientState {
  businessClient: IBusinessClient | null;
  businessClientList: IShortBusinessClient[];
  allBusinessClients: { id: number; name: string }[];
  currentPage: number;
  lastPage: number;
  total: number;
  perPage: number;
  errors: IErrors;
}

const state: BusinessClientState = {
  businessClient: null,
  businessClientList: [],
  allBusinessClients: [],
  currentPage: PAGINATION_FIRST_PAGE,
  lastPage: PAGINATION_DEFAULT_LAST_PAGE,
  total: PAGINATION_DEFAULT_TOTAL,
  perPage: PAGINATION_DEFAULT_PER_PAGE,
  errors: {},
};

const getters: GetterTree<BusinessClientState, RootState> = {
  businessClient: (state) => state.businessClient,
  businessClientList: (state) => state.businessClientList,
  allBusinessClients: (state) => state.allBusinessClients,
  currentPage: (state) => state.currentPage,
  lastPage: (state) => state.lastPage,
  total: (state) => state.total,
  perPage: (state) => state.perPage,
  errors: (state) => state.errors,
};

const actions: ActionTree<BusinessClientState, RootState> = {

  fetchBusinessClients: ({ commit }, filters): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'business-clients',
      method: 'GET',
      params: filters,
    }),
    commit, 'businessClientsLoaded'
  ),

  clearBusinessClients: ({ commit }) => commit('clearBusinessClients'),

  fetchAllBusinessClients: ({ commit }): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'business-clients/all',
      method: 'GET',
    }),
    commit, 'allBusinessClientsLoaded'
  ),

  fetchBusinessClient: ({ commit }, clientId): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'business-clients/' + clientId,
    }),
    commit, 'businessClientLoaded'
  ),

  resetBusinessClient: ({ commit }) => commit('businessClientLoaded', null),

  createBusinessClient: ({ commit }, data): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'business-clients',
      method: 'POST',
      data,
    }),
    commit, 'businessClientCreated'
  ),

  updateBusinessClient: ({ commit }, { clientId, data }: { clientId: number; data: any }): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'business-clients/' + clientId,
      method: 'PATCH',
      data,
    }),
    commit, 'businessClientUpdated'
  ),

  errors: ({ commit }, errors: IErrors): any => commit('errors', errors),
  cleanError: ({ commit }, index: any): any => commit('cleanError', index),

};

const mutations: MutationTree<BusinessClientState> = {

  businessClientsLoaded(state, payload: any) {
    if (!payload) {
      state.businessClientList = [];
      return;
    }
    state.businessClientList = Array.isArray(payload.data) ? payload.data : (Array.isArray(payload) ? payload : []);
    state.currentPage = payload.current_page || PAGINATION_FIRST_PAGE;
    state.lastPage = payload.last_page || PAGINATION_DEFAULT_LAST_PAGE;
    state.total = payload.total || PAGINATION_DEFAULT_TOTAL;
    state.perPage = payload.per_page || PAGINATION_DEFAULT_PER_PAGE;
  },

  allBusinessClientsLoaded(state, payload: any) {
    state.allBusinessClients = Array.isArray(payload) ? payload : (payload && Array.isArray(payload.data) ? payload.data : []);
  },

  clearBusinessClients(state) {
    state.businessClientList = [];
    state.currentPage = PAGINATION_FIRST_PAGE;
    state.lastPage = PAGINATION_DEFAULT_LAST_PAGE;
    state.total = PAGINATION_DEFAULT_TOTAL;
  },

  businessClientLoaded(state, payload: IBusinessClient | null) {
    state.businessClient = payload;
  },

  businessClientCreated(state, payload: IBusinessClient) {
    state.businessClient = payload;
  },

  businessClientUpdated(state, payload: IBusinessClient) {
    state.businessClient = payload;
    const idx = state.businessClientList.findIndex(c => c.id === payload.id);
    if (idx !== -1) {
      Vue.set(state.businessClientList, idx, payload);
    }
  },

  errors(state, payload: IErrors) {
    state.errors = payload;
  },

  cleanError(state, index: string) {
    Vue.delete(state.errors, index);
  },

};

const namespaced: boolean = true;

export const businessClientModule: Module<BusinessClientState, RootState> = {
  namespaced,
  state,
  getters,
  actions,
  mutations,
};
